import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import type { TeamMember } from "../../lib/types";
import { formatPhone } from "../../lib/phoneFormat";
import { useRealtimeDealChanges } from "../../lib/useRealtimeTable";

/**
 * One deal's comms thread — loader, unified timeline, the list and the compose
 * box — lifted out of the deal page's Comms tab (Oct 16, 2026) so the Finance →
 * Supplements board can open the same thread in a drawer without leaving the
 * board. CommsTab keeps its AI-summary card and filter bar and composes these
 * pieces in the same order as before; boards render the default export.
 *
 * Live: twilio_messages and activity_log rows for the deal (both are in the
 * supabase_realtime publication; twilio_calls is not) refetch the thread after an
 * 800 ms debounce, so an inbound reply shows up while the thread is open. The
 * later comms engine's per-deal strip should build on `useCommsTimeline`.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TwilioMessage {
  id: string;
  deal_id: string;
  direction: string; // "inbound" | "outbound"
  from_number: string;
  to_number: string;
  body: string;
  tag: string | null;
  status: string | null;
  num_media: number | null;
  media_urls: string[] | null;
  created_by: string | null;
  sent_at: string | null;
  delivered_at: string | null;
  replied_by_claude: boolean | null;
  created_at: string;
}

export interface TwilioCall {
  id: string;
  deal_id: string;
  direction: string;
  from_number: string;
  to_number: string;
  status: string | null;
  duration_seconds: number | null;
  answered_by_user_id: string | null;
  recording_url: string | null;
  transcription: string | null;
  claude_summary: string | null;
  voicemail: boolean | null;
  started_at: string | null;
  ended_at: string | null;
  created_at: string;
}

export interface ActivityEntry {
  id: string;
  deal_id: string;
  activity_type: string;
  title: string;
  description: string | null;
  metadata: Record<string, unknown> | null;
  performed_by: string | null;
  performed_at: string;
  direction: string | null;
  from_value: string | null;
  to_value: string | null;
}

// Unified timeline entry
export interface TimelineEntry {
  id: string;
  timestamp: string;
  source: "text" | "call" | "activity";
  icon: string;
  label: string;
  detail: string;
  secondary?: string;
  expandable?: boolean;
  fullText?: string;
  mediaUrls?: string[];
  recordingUrl?: string | null;
  claudeSummary?: string | null;
  performer?: string;
  direction?: string;
  activityType?: string;
}

export type CommsFilter = "all" | "texts" | "calls" | "notes";

export interface CommsCounts {
  all: number;
  texts: number;
  calls: number;
  notes: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function fmtTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function fmtDuration(sec: number): string {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s}s`;
}

function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return text.substring(0, max) + "...";
}

const ACTIVITY_ICONS: Record<string, string> = {
  stage_change: "→",
  note: "💬",
  call: "📞",
  text_sent: "📱",
  text_received: "📱",
  email_sent: "✉️",
  email_received: "✉️",
  file_uploaded: "📎",
  payment_received: "💵",
  payment_sent: "💵",
  invoice_sent: "📄",
  commission_event: "💰",
  assignment_change: "👤",
  field_change: "✏️",
  system_event: "⚙️",
  portal_view: "👁️",
  salesrabbit_sync: "📍",
};

/** The tables whose changes refetch an open thread. twilio_calls is not published, so a new call shows on the next open. */
const LIVE_TABLES = ["twilio_messages", "activity_log"] as const;

/** Texts + calls + activity rows merged into one list, newest first. */
export function buildTimeline(
  messages: TwilioMessage[],
  calls: TwilioCall[],
  activity: ActivityEntry[],
  members: Record<string, TeamMember>,
): TimelineEntry[] {
  const entries: TimelineEntry[] = [];

  // Texts
  for (const m of messages) {
    const isInbound = m.direction === "inbound";
    // Written on the homeowner status page (ho-portal-notify), not texted.
    const fromStatusPage = isInbound && m.tag === "portal_message";
    entries.push({
      id: `msg-${m.id}`,
      timestamp: m.created_at,
      source: "text",
      icon: fromStatusPage ? "💬" : "📱",
      label: fromStatusPage ? "Message from the status page"
        : isInbound ? "Inbound Text" : m.tag ? `Auto Text (${m.tag})` : "Outbound Text",
      detail: truncate(m.body, 120),
      expandable: m.body.length > 120,
      fullText: m.body,
      mediaUrls: m.num_media && m.media_urls ? m.media_urls : undefined,
      direction: m.direction,
      performer: m.created_by ? members[m.created_by]?.full_name : undefined,
      activityType: isInbound ? "text_received" : "text_sent",
    });
  }

  // Calls
  for (const c of calls) {
    const isInbound = c.direction === "inbound";
    const answeredBy = c.answered_by_user_id ? members[c.answered_by_user_id]?.full_name : null;
    entries.push({
      id: `call-${c.id}`,
      timestamp: c.started_at ?? c.created_at,
      source: "call",
      icon: "📞",
      label: isInbound
        ? c.voicemail ? "Voicemail" : "Inbound Call"
        : "Outbound Call",
      detail: [
        c.duration_seconds ? fmtDuration(c.duration_seconds) : null,
        answeredBy ? `${answeredBy} answered` : null,
        c.status === "no-answer" ? "No answer" : null,
        c.voicemail ? "Left voicemail" : null,
      ].filter(Boolean).join(" — "),
      secondary: c.claude_summary ?? c.transcription ?? undefined,
      expandable: !!(c.transcription && c.transcription.length > 100),
      fullText: c.transcription ?? undefined,
      recordingUrl: c.recording_url,
      claudeSummary: c.claude_summary,
      direction: c.direction,
      activityType: "call",
    });
  }

  // Activity log (skip text_sent/text_received since we have full twilio_messages)
  for (const a of activity) {
    if (a.activity_type === "text_sent" || a.activity_type === "text_received") continue;
    // Also skip calls if we have twilio_calls
    if (a.activity_type === "call") continue;

    entries.push({
      id: `act-${a.id}`,
      timestamp: a.performed_at,
      source: "activity",
      icon: ACTIVITY_ICONS[a.activity_type] ?? "•",
      label: a.title,
      detail: a.description ?? "",
      performer: a.performed_by ? members[a.performed_by]?.full_name : undefined,
      direction: a.direction ?? undefined,
      activityType: a.activity_type,
    });
  }

  // Sort by timestamp descending
  entries.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  return entries;
}

export function filterTimeline(timeline: TimelineEntry[], filter: CommsFilter): TimelineEntry[] {
  if (filter === "all") return timeline;
  if (filter === "texts") return timeline.filter((e) => e.source === "text");
  if (filter === "calls") return timeline.filter((e) => e.source === "call");
  if (filter === "notes") return timeline.filter((e) =>
    e.source === "activity" && (e.activityType === "note" || e.activityType === "stage_change" || e.activityType === "file_uploaded")
  );
  return timeline;
}

// ---------------------------------------------------------------------------
// Data hook
// ---------------------------------------------------------------------------

/**
 * Loads a deal's texts, calls and activity rows and builds the timeline. `loading`
 * is true only for the first load of a deal; live refetches update in place so the
 * list never flashes or loses its expanded rows. `appendMessage` adds a just-sent
 * text locally (the realtime refetch then replaces it with the stored row).
 */
export function useCommsTimeline({
  dealId,
  members,
  live = true,
}: {
  dealId: string;
  members: Record<string, TeamMember>;
  live?: boolean;
}) {
  const [messages, setMessages] = useState<TwilioMessage[]>([]);
  const [calls, setCalls] = useState<TwilioCall[]>([]);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const loadedDealRef = useRef<string | null>(null);

  // Load all comms data
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (loadedDealRef.current !== dealId) setLoading(true);
      const [
        { data: msgs },
        { data: cls },
        { data: acts },
      ] = await Promise.all([
        supabase
          .from("twilio_messages")
          .select("*")
          .eq("deal_id", dealId)
          .order("created_at", { ascending: false })
          .limit(200),
        supabase
          .from("twilio_calls")
          .select("*")
          .eq("deal_id", dealId)
          .order("created_at", { ascending: false })
          .limit(100),
        supabase
          .from("activity_log")
          .select("*")
          .eq("deal_id", dealId)
          .order("performed_at", { ascending: false })
          .limit(300),
      ]);
      if (!cancelled) {
        setMessages((msgs ?? []) as TwilioMessage[]);
        setCalls((cls ?? []) as TwilioCall[]);
        setActivity((acts ?? []) as ActivityEntry[]);
        loadedDealRef.current = dealId;
        setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [dealId, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  useRealtimeDealChanges(live ? dealId : null, LIVE_TABLES, reload);

  const timeline = useMemo(
    () => buildTimeline(messages, calls, activity, members),
    [messages, calls, activity, members],
  );

  const counts = useMemo<CommsCounts>(() => ({
    all: timeline.length,
    texts: timeline.filter((e) => e.source === "text").length,
    calls: timeline.filter((e) => e.source === "call").length,
    notes: timeline.filter((e) => e.source === "activity").length,
  }), [timeline]);

  const appendMessage = useCallback((m: TwilioMessage) => {
    setMessages((prev) => [m, ...prev]);
  }, []);

  return { timeline, counts, loading, appendMessage, reload };
}

// ---------------------------------------------------------------------------
// Compose box
// ---------------------------------------------------------------------------

/**
 * Sends a text to the homeowner through twilio-send-sms (tag portal_manual, so
 * it lands in the deal's comms log like any Comms-tab text) and hands the sent
 * message back through `onSent`.
 */
export function CommsCompose({
  dealId,
  homeownerPhone,
  onSent,
  compact = false,
  autoFocus = false,
}: {
  dealId: string;
  homeownerPhone: string | null;
  onSent?: (message: TwilioMessage) => void;
  /** Slim box for the board drawer: no banner, smaller text. */
  compact?: boolean;
  autoFocus?: boolean;
}) {
  const { member } = useAuth();
  const [composing, setComposing] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  // Send a text from the compose box
  const sendText = useCallback(async () => {
    if (!composing.trim() || sending) return;
    setSending(true);
    setSendError(null);
    try {
      const { data, error } = await supabase.functions.invoke("twilio-send-sms", {
        body: { to: homeownerPhone, body: composing.trim(), deal_id: dealId, tag: "portal_manual", sent_by: member?.id ?? null },
      });
      if (error) {
        setSendError(error.message || "Send failed");
        setSending(false);
        return;
      }
      if (data?.error) { setSendError(data.error); setSending(false); return; }

      // Add message to local state
      const newMsg: TwilioMessage = {
        id: data?.message?.id ?? crypto.randomUUID(),
        deal_id: dealId,
        direction: "outbound",
        from_number: "+14708238816",
        to_number: homeownerPhone ?? "",
        body: composing.trim(),
        tag: "portal_manual",
        status: "sent",
        num_media: null,
        media_urls: null,
        created_by: member?.id ?? null,
        sent_at: new Date().toISOString(),
        delivered_at: null,
        replied_by_claude: null,
        created_at: new Date().toISOString(),
      };
      onSent?.(newMsg);
      setComposing("");
    } catch (err) {
      setSendError(String(err));
    }
    setSending(false);
  }, [composing, sending, dealId, homeownerPhone, member, onSent]);

  const handleComposeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendText(); }
  };

  const sendIcon = sending ? (
    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  ) : (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
    </svg>
  );

  const box = (
    <>
      {sendError && (
        <div className="text-xs text-red-600 bg-red-50 rounded px-3 py-1.5 mb-2">{sendError}</div>
      )}
      <div className="flex gap-2">
        <textarea
          value={composing}
          onChange={(e) => setComposing(e.target.value)}
          onKeyDown={handleComposeKeyDown}
          placeholder="Type a message... (Enter to send)"
          className={
            "flex-1 resize-none rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-bardin-teal/30 focus:border-bardin-teal placeholder-slate-400 " +
            (compact ? "text-[13px]" : "text-sm")
          }
          rows={2}
          disabled={sending}
          autoFocus={autoFocus}
        />
        <button
          onClick={sendText}
          disabled={!composing.trim() || sending}
          className="self-end px-4 py-2 rounded-lg bg-bardin-teal text-white text-sm font-medium hover:bg-bardin-teal/90 transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
        >
          {sendIcon}
          Send
        </button>
      </div>
    </>
  );

  if (compact) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-2">
        {!homeownerPhone ? (
          <div className="text-xs text-slate-400 italic">No phone number on this deal</div>
        ) : (
          box
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow overflow-hidden">
      <div className="px-4 py-2.5 bg-gradient-to-r from-bardin-teal to-emerald-600 text-white">
        <span className="text-sm font-semibold">Send Text to Homeowner</span>
        {homeownerPhone && <span className="text-xs ml-2 text-white/70">{formatPhone(homeownerPhone)}</span>}
      </div>
      <div className="p-3">
        {!homeownerPhone ? (
          <div className="text-sm text-slate-400 italic">No phone number on this deal</div>
        ) : (
          box
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Timeline list
// ---------------------------------------------------------------------------

export function CommsTimelineList({
  entries,
  compact = false,
  emptyLabel = "No communications found for this deal.",
  pageSize = 50,
}: {
  entries: TimelineEntry[];
  /** Tighter rows for the board drawer. */
  compact?: boolean;
  emptyLabel?: string;
  pageSize?: number;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [showCount, setShowCount] = useState(pageSize);

  const visible = entries.slice(0, showCount);
  const hasMore = entries.length > showCount;

  function toggleExpand(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <>
      {/* Timeline */}
      {visible.length === 0 ? (
        <div className={"bg-white rounded-xl shadow text-center text-sm text-slate-400 " + (compact ? "p-4" : "p-6")}>
          {emptyLabel}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow divide-y divide-slate-100">
          {visible.map((entry) => {
            const isExpanded = expanded.has(entry.id);
            const isInbound = entry.direction === "inbound";

            return (
              <div key={entry.id} className={(compact ? "px-3 py-2" : "px-4 py-3") + " hover:bg-slate-50/50 transition"}>
                <div className={compact ? "flex gap-2" : "flex gap-3"}>
                  {/* Icon */}
                  <div className={`rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    compact ? "w-6 h-6 text-xs" : "w-8 h-8 text-sm"
                  } ${
                    entry.source === "text"
                      ? isInbound
                        ? "bg-blue-50 text-blue-600"
                        : "bg-emerald-50 text-emerald-600"
                      : entry.source === "call"
                        ? "bg-purple-50 text-purple-600"
                        : "bg-slate-100 text-slate-500"
                  }`}>
                    {entry.icon}
                  </div>

                  {/* Content */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={(compact ? "text-[12px]" : "text-sm") + " font-medium text-bardin-ink"}>{entry.label}</span>
                        {isInbound && entry.source === "text" && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-600 font-medium">IN</span>
                        )}
                        {!isInbound && entry.source === "text" && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-600 font-medium">OUT</span>
                        )}
                        {entry.performer && (
                          <span className="text-xs text-slate-400">by {entry.performer}</span>
                        )}
                      </div>
                      <span className={(compact ? "text-[10px]" : "text-xs") + " text-slate-400 flex-shrink-0 tabular-nums"}>
                        {fmtTime(entry.timestamp)}
                      </span>
                    </div>

                    {/* Main detail text */}
                    {entry.detail && (
                      <div className={`${compact ? "text-[13px]" : "text-sm"} mt-1 ${
                        entry.source === "text" ? "text-slate-700" : "text-slate-500"
                      }`}>
                        {entry.expandable && !isExpanded
                          ? entry.detail
                          : entry.fullText ?? entry.detail}
                      </div>
                    )}

                    {/* Claude summary for calls */}
                    {entry.claudeSummary && (
                      <div className="mt-2 px-3 py-2 bg-bardin-sky/30 rounded-lg border border-bardin-sky/50">
                        <div className="text-[10px] uppercase tracking-wider text-bardin-teal font-medium mb-1">AI Summary</div>
                        <div className="text-sm text-slate-700">{entry.claudeSummary}</div>
                      </div>
                    )}

                    {/* Secondary info (call transcription) */}
                    {entry.secondary && !entry.claudeSummary && (
                      <div className="text-xs text-slate-400 mt-1 italic">
                        {isExpanded ? entry.fullText ?? entry.secondary : truncate(entry.secondary, 80)}
                      </div>
                    )}

                    {/* Media previews */}
                    {entry.mediaUrls && entry.mediaUrls.length > 0 && (
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {entry.mediaUrls.map((url, i) => (
                          <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block">
                            <div className="w-16 h-16 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-xs text-slate-400 hover:border-bardin-teal transition overflow-hidden">
                              {url.match(/\.(jpg|jpeg|png|gif|webp|heic)/i)
                                ? <img src={url} alt="" className="w-full h-full object-cover" />
                                : <span className="text-center px-1">View file</span>
                              }
                            </div>
                          </a>
                        ))}
                      </div>
                    )}

                    {/* Recording player */}
                    {entry.recordingUrl && (
                      <div className="mt-2">
                        <audio controls preload="none" className="h-8 w-full max-w-xs">
                          <source src={entry.recordingUrl} type="audio/mpeg" />
                        </audio>
                      </div>
                    )}

                    {/* Expand/collapse */}
                    {entry.expandable && (
                      <button
                        onClick={() => toggleExpand(entry.id)}
                        className="text-xs text-bardin-teal hover:underline mt-1"
                      >
                        {isExpanded ? "Show less" : "Show more"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Load more */}
      {hasMore && (
        <div className="text-center">
          <button
            onClick={() => setShowCount((c) => c + pageSize)}
            className="px-4 py-2 text-sm text-bardin-teal hover:bg-bardin-teal/5 rounded-lg transition"
          >
            Load more ({entries.length - showCount} remaining)
          </button>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Thread = compose + live timeline (what a board drawer renders)
// ---------------------------------------------------------------------------

export default function CommsThread({
  dealId,
  homeownerPhone,
  members,
  compact = false,
  filter = "all",
  onSent,
  autoFocusCompose = false,
}: {
  dealId: string;
  homeownerPhone: string | null;
  members: Record<string, TeamMember>;
  /** Tight spacing and a slim compose box, for a drawer. */
  compact?: boolean;
  filter?: CommsFilter;
  /** Fires after a text goes out from this thread's compose box. */
  onSent?: (message: TwilioMessage) => void;
  autoFocusCompose?: boolean;
}) {
  const { timeline, loading, appendMessage } = useCommsTimeline({ dealId, members });
  const entries = useMemo(() => filterTimeline(timeline, filter), [timeline, filter]);
  const handleSent = useCallback((m: TwilioMessage) => {
    appendMessage(m);
    onSent?.(m);
  }, [appendMessage, onSent]);

  return (
    <div className={compact ? "space-y-2" : "space-y-4"}>
      <CommsCompose
        dealId={dealId}
        homeownerPhone={homeownerPhone}
        compact={compact}
        autoFocus={autoFocusCompose}
        onSent={handleSent}
      />
      {loading ? (
        <div className="text-center py-8 text-slate-400">
          <div className="animate-pulse">Loading communications...</div>
        </div>
      ) : (
        <CommsTimelineList entries={entries} compact={compact} />
      )}
    </div>
  );
}
